import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import Countdown from "@/components/Countdown";

const lineVariants = {
  hidden: { y: "115%", rotate: 2 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { delay: 0.55 + i * 0.2, duration: 1.2, ease: [0.16, 1, 0.3, 1] },
  }),
};

const testIds = ["hero-motto-chercher", "hero-motto-creer", "hero-motto-transmettre"];

const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.22]);
  const fade = useTransform(scrollY, [0, 600], [1, 0]);

  const go = (id) => {
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { duration: 1.4 });
  };

  return (
    <section id="hero" data-testid="hero-section" className="grain relative flex min-h-[100svh] items-center overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/images/lieux/baie-abbaye-01.jpeg"
          alt="Baie du Mont-Saint-Michel et abbaye Saint-Léonard"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/55 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(13,12,10,0.55)_100%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-4 pb-24 pt-36 sm:px-8 lg:px-16">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.9 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-gold sm:text-xs"
        >
          {t.hero.kicker}
        </motion.p>

        <motion.h2
          data-testid="hero-overture"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45, duration: 1 }}
          className="mb-4 font-serif text-xl italic text-parchment/85 sm:text-2xl"
        >
          {t.hero.overture}
        </motion.h2>

        <h1 className="font-serif font-light leading-[0.95] text-parchment">
          {t.hero.words.map((word, i) => (
            <span key={word} className="block overflow-hidden pb-1">
              <motion.span
                data-testid={testIds[i]}
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block origin-left text-[15vw] tracking-tight sm:text-[11vw] lg:text-[8.5vw]"
              >
                {word}
                <span className="text-gold">{i === 2 ? "" : ","}</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-parchment/80 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.75, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-discover"
            onClick={() => go("manifeste")}
            className="group relative overflow-hidden rounded-full border border-gold/60 px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:text-ink"
          >
            <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
            <span className="relative">{t.hero.cta_primary}</span>
          </button>
          <button
            data-testid="hero-cta-residents"
            onClick={() => go("residents")}
            className="rounded-full px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-parchment/70 underline decoration-gold/40 underline-offset-8 transition-colors duration-300 hover:text-gold"
          >
            {t.hero.cta_secondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0, duration: 1 }}
          onClick={() => go("inauguration")}
          role="button"
          tabIndex={0}
          className="mt-12 inline-flex cursor-pointer items-center gap-3 rounded-full border border-gold/40 bg-ink/40 px-6 py-3 backdrop-blur-md transition-colors duration-300 hover:border-gold"
        >
          <span aria-hidden="true" className="font-display text-[10px] text-gold">✦</span>
          <span data-testid="hero-inauguration" className="font-mono text-[10px] uppercase tracking-[0.3em] text-parchment/90 sm:text-[11px]">
            {t.hero.inauguration}
          </span>
          <span aria-hidden="true" className="font-display text-[10px] text-gold">✦</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
        >
          <Countdown />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-parchment/50">{t.hero.scroll}</span>
        <div className="h-14 w-px overflow-hidden bg-parchment/15">
          <div className="animate-scroll-line h-full w-full bg-gold" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
